import { useContext, useState } from "react";
import styled, { useTheme } from "styled-components";
import Button from "common/Buttons/Button";
import { newLookType } from "common/NewLook/tokens";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { AnalyticsContext } from "analytics/context";
import { OnboardingStepValueType } from "analytics/events";
import { getAnalyticsBotSource } from "utils/misc";
import LinkButton from "common/Buttons/LinkButton";
import Modal from "common/Modal/Modal";
import { OnboardingStyles } from "features/onboarding/common/styles";
import {
  ONBOARDING_CANCEL_SKIP_BUTTON_ID,
  ONBOARDING_CONFIRM_SKIP_BUTTON_ID,
  ONBOARDING_EXPLORE_ID,
  ONBOARDING_SKIP_MODAL_BODY_ID,
  ONBOARDING_SKIP_MODAL_ID,
  ONBOARDING_SKIP_MODAL_TITLE_ID,
} from "testIds.ts";

interface ExplorePlatformButtonProps {
  onboardingStep: OnboardingStepValueType;
}

const ExplorePlatformButton = ({
  onboardingStep,
}: ExplorePlatformButtonProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { onSkipOnboarding, finishOnboardingLoading } =
    useContext(OnboardingContext)!;
  const { analytics } = useContext(AnalyticsContext);
  const theme = useTheme();

  const trackSkip = () =>
    analytics?.onboardingSkipped({
      onboardingStep,
      tradingMethodType: undefined,
      botSource: getAnalyticsBotSource(),
    });

  return (
    <>
      <LinkButton
        type="link"
        style={{ fontSize: 13, fontWeight: 400, height: "auto", padding: 0 }}
        $color={theme.colors.secondaryText}
        onClick={() => setIsConfirmationOpen(true)}
        data-testid={ONBOARDING_EXPLORE_ID}
      >
        {t("skip_messages.want_to_explore")}
      </LinkButton>
      <Modal
        open={isConfirmationOpen}
        footer={false}
        width={420}
        title={
          <OnboardingStyles.ModalTitle
            data-testid={ONBOARDING_SKIP_MODAL_TITLE_ID}
            tabIndex={-1}
          >
            {t("skip_modal.title")}
          </OnboardingStyles.ModalTitle>
        }
        styles={{
          header: { borderBottom: "none", padding: "24px 24px 0" },
          body: { padding: "8px 24px 24px" },
        }}
        onCancel={() => setIsConfirmationOpen(false)}
        data-testid={ONBOARDING_SKIP_MODAL_ID}
      >
        <ModalBody data-testid={ONBOARDING_SKIP_MODAL_BODY_ID}>
          {t("revision.skip_setup_confirmation")}
        </ModalBody>
        <ModalActions>
          <Button
            onClick={() => onSkipOnboarding?.(trackSkip)}
            loading={finishOnboardingLoading}
            data-testid={ONBOARDING_CONFIRM_SKIP_BUTTON_ID}
          >
            {t("skip_modal.yes_button")}
          </Button>
          <Button
            type="primary"
            onClick={() => setIsConfirmationOpen(false)}
            data-testid={ONBOARDING_CANCEL_SKIP_BUTTON_ID}
          >
            {t("skip_modal.no_button")}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default ExplorePlatformButton;

const ModalBody = styled.div`
  ${newLookType.body};
  color: ${(p) => p.theme.colors.secondaryText};
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    > .ant-btn {
      width: 100%;
    }
  }
`;
